import React, { useCallback, useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { toast } from "sonner";
import {
  Loader2, ShieldAlert, Download, Play, Upload, Info,
  CheckCircle2, XCircle,
} from "lucide-react";

// -----------------------------------------------------------------------------
// DAAM — Pro Quality Gate (Feb 2026).
//
// One-shot Sora 2 Pro image-to-video evaluation. Model, resolution, and
// duration are LOCKED at the backend — this page is intentionally minimal.
// See /app/backend/pro_quality_gate.py for the contract.
// -----------------------------------------------------------------------------

const API_BASE   = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY  = "nx_token";
const PENDING_KEY = "daam_pqg_pending_job";
const IDEMP_KEY   = "daam_pqg_pending_idemp";
const HISTORY_KEY = "daam_pqg_history";
const HISTORY_MAX = 5;
const POLL_MS     = 3000;
const TERMINAL    = ["ready", "failed"];

const getToken = () => localStorage.getItem(TOKEN_KEY) || "";

async function api(path, opts = {}) {
  const isForm = opts.body instanceof FormData;
  const r = await fetch(`${API_BASE}${path}`, {
    ...opts,
    credentials: "include",
    headers: {
      ...(isForm ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${getToken()}`,
      ...(opts.headers || {}),
    },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const d = body?.detail;
    const msg = (d && (d.message_ar || d.message_en || d.message)) ||
                body?.message || r.statusText || "Request failed";
    const err = new Error(msg);
    err.code = d?.code;
    err.status = r.status;
    throw err;
  }
  return body;
}

const uuid = () =>
  (crypto.randomUUID && crypto.randomUUID()) ||
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const rr = (Math.random() * 16) | 0;
    return (c === "x" ? rr : (rr & 0x3) | 0x8).toString(16);
  });

const readAsDataURL = (file) => new Promise((res, rej) => {
  const fr = new FileReader();
  fr.onload = () => res(fr.result);
  fr.onerror = rej;
  fr.readAsDataURL(file);
});

const DAAM_AD_SCENARIO_DEFAULT = `إعلان سينمائي فاخر لمنصة دعم داخل استوديو تسويقي حديث في المملكة العربية السعودية. فريق سعودي محترف من الشباب يتعاون على تخطيط المحتوى، إدارة الحملات، الإنتاج الإبداعي، وإدارة حسابات التواصل الاجتماعي. الكاميرا تتحرك بسلاسة داخل الاستوديو لتُظهر الفريق يناقش الأفكار، يراجع الحملات، يصمم المحتوى، ويحلل الأداء حول محطات عمل أنيقة. تنتهي اللقطة بتركيبة نظيفة تصلح لإضافة هوية دعم لاحقاً. يُظهر الإعلان أن دعم تجمع تخطيط المحتوى، الإنتاج، التسويق وإدارة الحسابات في منصة واحدة.`;

export default function ProQualityGatePage() {
  const [config, setConfig] = useState(null);
  const [configErr, setConfigErr] = useState(null);
  const [refFile, setRefFile] = useState(null);
  const [refId, setRefId] = useState(null);
  const [refPreviewUrl, setRefPreviewUrl] = useState(null);
  const [concept, setConcept] = useState(DAAM_AD_SCENARIO_DEFAULT);
  const [compiled, setCompiled] = useState(null);      // { compiled_prompt, estimated_sar, ... }
  const [editedPrompt, setEditedPrompt] = useState("");
  const [confirmPrompt, setConfirmPrompt] = useState(false);
  const [confirmCharge, setConfirmCharge] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);
  const pending = job && !TERMINAL.includes(job.status);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const pushHistory = useCallback((doc) => {
    if (!doc || !doc.job_id) return;
    setHistory((prev) => {
      const filtered = prev.filter((h) => h.job_id !== doc.job_id);
      const next = [doc, ...filtered].slice(0, HISTORY_MAX);
      try { localStorage.setItem(HISTORY_KEY, JSON.stringify(next)); } catch { /* ignore quota */ }
      return next;
    });
  }, []);

  const pollOnce = useCallback(async (jobId) => {
    try {
      const doc = await api(`/api/preview/video-pilot/job/${jobId}`);
      setJob(doc);
      if (TERMINAL.includes(doc.status)) {
        stopPolling();
        localStorage.removeItem(PENDING_KEY);
        localStorage.removeItem(IDEMP_KEY);
        pushHistory(doc);
        if (doc.status === "ready") toast.success("اكتمل الاختبار. اضغط تشغيل أو تنزيل.");
        else                        toast.error(doc.error_message || "فشل التوليد — لا إعادة محاولة تلقائية.");
      }
    } catch (e) {
      if (e.status === 404) {
        stopPolling();
        localStorage.removeItem(PENDING_KEY);
        localStorage.removeItem(IDEMP_KEY);
        setJob(null);
      }
    }
  }, [stopPolling, pushHistory]);

  const startPolling = useCallback((jobId) => {
    stopPolling();
    pollOnce(jobId);
    pollRef.current = setInterval(() => pollOnce(jobId), POLL_MS);
  }, [pollOnce, stopPolling]);

  // Hydrate the locked config + safely recover any in-flight job on mount.
  // Terminal jobs from a previous session must NEVER become the current active
  // test — they are moved into the read-only "Previous Attempts" history and
  // both stale localStorage keys are cleared.
  useEffect(() => {
    api("/api/preview/video-pilot/pro-gate/config")
      .then(setConfig)
      .catch((e) => setConfigErr(e.message));

    // Load persisted history first so terminal hydration can append to it.
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setHistory(parsed.slice(0, HISTORY_MAX));
      }
    } catch { /* ignore malformed history */ }

    const pendingId = localStorage.getItem(PENDING_KEY);
    if (pendingId) {
      // Fetch once — do NOT start the poll interval until we know it's alive.
      api(`/api/preview/video-pilot/job/${pendingId}`)
        .then((doc) => {
          if (TERMINAL.includes(doc.status)) {
            // Stale terminal job from a prior session: clear keys, park in history.
            localStorage.removeItem(PENDING_KEY);
            localStorage.removeItem(IDEMP_KEY);
            pushHistory(doc);
            // Explicitly do NOT setJob(doc) — active area stays empty for a new test.
          } else {
            // Genuinely in-flight — resume polling.
            setJob(doc);
            startPolling(pendingId);
          }
        })
        .catch(() => {
          // 404 or network — treat as stale.
          localStorage.removeItem(PENDING_KEY);
          localStorage.removeItem(IDEMP_KEY);
        });
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- upload reference image ---------------------------------------------
  const [cropPreviewUrl, setCropPreviewUrl] = useState(null);
  const [cropDims, setCropDims] = useState(null);
  const [origDims, setOrigDims] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError("حجم الصورة يتجاوز 8 ميجابايت.");
      return;
    }
    setError(null);
    setUploadBusy(true);
    setRefId(null);
    setCropPreviewUrl(null);
    setCompiled(null);
    setConfirmPrompt(false);
    setConfirmCharge(false);
    try {
      const data_url = await readAsDataURL(file);
      const r = await api("/api/preview/video-pilot/pro-gate/reference", {
        method: "POST", body: JSON.stringify({ data_url }),
      });
      if (!r.crop_preview_url) {
        setError(`تعذّر إعداد قصّة عمودية 1024×1792 من هذه الصورة: ${r.crop_error || "غير معروف"}`);
        return;
      }
      setRefId(r.reference_image_id);
      setRefPreviewUrl(`${API_BASE}${r.preview_url}`);
      setCropPreviewUrl(`${API_BASE}${r.crop_preview_url}`);
      setCropDims(r.crop_dims);
      setOrigDims(r.original_dims);
      setRefFile(file);
      toast.success("تم رفع الصورة وإعداد القصّة العمودية 1024×1792.");
    } catch (e) { setError(e.message); }
    finally     { setUploadBusy(false); }
  };

  // ---- compile prompt + cost preview (no charge) --------------------------
  const handleCompile = async () => {
    if (!concept.trim()) return;
    setError(null);
    setPreviewBusy(true);
    setConfirmPrompt(false);
    setConfirmCharge(false);
    try {
      // Text-to-video: do NOT send reference_image_id. The backend
      // refuses i2v with 423 while the Emergent contract is unconfirmed.
      const r = await api("/api/preview/video-pilot/pro-gate/preview", {
        method: "POST",
        body: JSON.stringify({ concept }),
      });
      setCompiled(r);
      setEditedPrompt(r.compiled_prompt);
    } catch (e) { setError(e.message); }
    finally     { setPreviewBusy(false); }
  };

  const canGenerate = !!(
    config && compiled &&
    editedPrompt.trim().length >= 20 &&
    confirmPrompt && confirmCharge &&
    compiled.cost_within_guard &&
    !pending &&
    !config.pro_gate_blocked
  );

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setError(null);
    let idempotency_key = localStorage.getItem(IDEMP_KEY);
    if (!idempotency_key) {
      idempotency_key = uuid();
      localStorage.setItem(IDEMP_KEY, idempotency_key);
    }
    try {
      const r = await api("/api/preview/video-pilot/pro-gate/generate", {
        method: "POST",
        body: JSON.stringify({
          concept,
          compiled_prompt: editedPrompt,
          idempotency_key,
          confirm_prompt: true,
          confirm_charge: true,
        }),
      });
      localStorage.setItem(PENDING_KEY, r.job_id);
      setJob({ job_id: r.job_id, status: r.status, prompt: editedPrompt });
      startPolling(r.job_id);
      if (r.idempotent_hit)
        toast.info("تم استرجاع نفس الطلب — لن يُصرف أي رصيد جديد.");
      else
        toast.success("بدأ التوليد. سيتم استطلاع الحالة تلقائياً.");
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setConfirmPrompt(false);
      setConfirmCharge(false);
    }
  };

  const clearJob = () => {
    stopPolling();
    localStorage.removeItem(PENDING_KEY);
    localStorage.removeItem(IDEMP_KEY);
    if (job && TERMINAL.includes(job.status)) pushHistory(job);
    setJob(null);
  };

  // Non-billable UI reset: clears any terminal active job into history,
  // wipes stale localStorage keys, and pre-provisions a fresh client-side
  // idempotency key so the next Generate click starts truly fresh.
  // Does NOT call any backend generation endpoint.
  const startNewTest = () => {
    stopPolling();
    if (job && TERMINAL.includes(job.status)) pushHistory(job);
    localStorage.removeItem(PENDING_KEY);
    localStorage.removeItem(IDEMP_KEY);
    try { localStorage.setItem(IDEMP_KEY, uuid()); } catch { /* ignore */ }
    setJob(null);
    setError(null);
    setConfirmPrompt(false);
    setConfirmCharge(false);
    toast.info("تم تجهيز اختبار جديد. لم يُصرف أي رصيد.");
  };

  const clearHistory = () => {
    setHistory([]);
    try { localStorage.removeItem(HISTORY_KEY); } catch { /* ignore */ }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto py-8 px-4 space-y-6" dir="rtl">
        <header data-testid="pqg-header" className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <ShieldAlert className="w-5 h-5 text-[#BC944A]" />
            <h1 className="text-2xl font-bold text-neutral-900">
              اختبار الجودة الاحترافية — إعلان دعم
            </h1>
            {!pending && (job || history.length > 0) && (
              <button
                data-testid="pqg-start-new-test-btn"
                onClick={startNewTest}
                className="ms-auto text-xs font-semibold px-3 py-1.5 rounded-full
                           border border-[#BC944A] text-[#BC944A] hover:bg-[#BC944A]
                           hover:text-white transition"
              >
                بدء اختبار جديد
              </button>
            )}
          </div>
          <p className="text-sm text-neutral-600">
            استدعاء واحد فقط. نص إلى فيديو، عمودي 1024×1792، 12 ثانية،
            بدون صورة مرجعية، بدون إعادة محاولة تلقائية. تم قفل الموديل
            والدقة والمدة على الخادم. التعليق الصوتي والعلامات التجارية
            تُضاف بعد التوليد بدون استدعاء جديد.
          </p>
        </header>

        {configErr && <ErrorBanner text={configErr} />}
        {config && config.pro_gate_blocked && (
          <IncidentBanner
            statusCode={config.pro_gate_status}
            messageAr={config.pro_gate_blocked_message_ar}
            incidentRef={config.pro_gate_incident_ref}
          />
        )}
        {config && <ConfigPanel c={config} />}

        <div data-testid="pqg-current-test-wrap" className="space-y-6">
          <div className="text-[11px] uppercase tracking-widest text-[#BC944A] font-semibold">
            الاختبار الحالي
          </div>

        {/* Step 1 — DAAM ad scenario */}
        <section data-testid="pqg-concept-section"
                 className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
          <StepHeader n={1} title="سيناريو الإعلان (يمكنك التعديل)" />
          <textarea
            data-testid="pqg-concept-input"
            value={concept}
            onChange={(e) => setConcept(e.target.value)}
            disabled={pending || (config && config.pro_gate_blocked)}
            rows={7}
            dir="rtl"
            className="w-full rounded-xl border border-neutral-300 p-3 text-sm
                       focus:outline-none focus:ring-2 focus:ring-[#BC944A]/40
                       disabled:bg-neutral-50 disabled:text-neutral-400"
          />
          <button
            data-testid="pqg-compile-btn"
            onClick={handleCompile}
            disabled={!concept.trim() || previewBusy || pending || (config && config.pro_gate_blocked)}
            className="px-5 py-2 rounded-full text-sm font-semibold bg-neutral-900
                       text-white disabled:bg-neutral-200 disabled:text-neutral-500
                       disabled:cursor-not-allowed">
            {previewBusy ? "جاري التركيب…" : "إنشاء البرومبت السينمائي وعرض التكلفة"}
          </button>
        </section>

        {/* Step 3 — Compiled prompt + cost preview */}
        {compiled && (
          <section data-testid="pqg-preview-section"
                   className="rounded-2xl border border-[#BC944A]/60 bg-[#FBF7EE]/40 p-5 space-y-4">
            <StepHeader n={2} title="راجع البرومبت والتكلفة قبل التأكيد" />

            <CostRow label="الموديل" value={compiled.model} testid="pqg-model" />
            <CostRow label="الدقة"  value={compiled.size} testid="pqg-size" />
            <CostRow label="المدة"  value={`${compiled.duration_seconds} ثوانٍ`} testid="pqg-duration" />
            <CostRow
              label="التكلفة الدقيقة"
              value={`${compiled.estimated_sar} SAR`}
              testid="pqg-cost"
              highlight={true}
              caption={`الحد الأقصى المسموح ${compiled.max_total_sar} SAR · ${compiled.pricing_source}`}
            />

            <div>
              <label className="text-xs text-neutral-600 mb-1 block">
                البرومبت النهائي (يمكنك تعديله يدوياً قبل التأكيد)
              </label>
              <textarea
                data-testid="pqg-compiled-prompt"
                value={editedPrompt}
                onChange={(e) => { setEditedPrompt(e.target.value); setConfirmPrompt(false); }}
                rows={10}
                dir="ltr"
                className="w-full rounded-xl border border-neutral-300 p-3
                           text-xs font-mono leading-relaxed
                           focus:outline-none focus:ring-2 focus:ring-[#BC944A]/40" />
            </div>

            <div className="space-y-2 pt-1">
              <ConfirmCheck
                testid="pqg-confirm-prompt"
                checked={confirmPrompt}
                onChange={setConfirmPrompt}
                label="أوافق على هذا البرومبت وأتحمّل مسؤولية النتيجة."
              />
              <ConfirmCheck
                testid="pqg-confirm-charge"
                checked={confirmCharge}
                onChange={setConfirmCharge}
                label={`أؤكّد صرف ${compiled.estimated_sar} SAR من رصيد Universal Key.`}
              />
            </div>

            <button
              data-testid="pqg-generate-btn"
              onClick={handleGenerate}
              disabled={!canGenerate}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition ${
                canGenerate
                  ? "bg-[#BC944A] text-white hover:bg-[#a58038]"
                  : "bg-neutral-200 text-neutral-500 cursor-not-allowed"}`}>
              {pending
                ? (<><Loader2 className="inline w-4 h-4 me-2 animate-spin" />قيد التوليد…</>)
                : "توليد الفيديو الآن"}
            </button>
            {pending && (
              <p className="text-xs text-neutral-600" data-testid="pqg-pending-line">
                يوجد اختبار قيد التنفيذ — لن يبدأ اختبار جديد حتى ينتهي هذا.
              </p>
            )}
          </section>
        )}

        {error && <ErrorBanner text={error} />}
        {job && <JobPanel job={job} apiBase={API_BASE} onDismiss={clearJob} />}
        </div>

        {history.length > 0 && (
          <section
            data-testid="pqg-history-section"
            className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/70 p-5 space-y-3 mt-8"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
                <Info className="w-4 h-4" />
                المحاولات السابقة
                <span className="text-[10px] font-normal text-neutral-500">
                  (للاطّلاع فقط — لا يعاد التوليد تلقائياً)
                </span>
              </div>
              <button
                data-testid="pqg-clear-history-btn"
                onClick={clearHistory}
                className="text-[11px] text-neutral-500 hover:text-neutral-800 underline"
              >
                مسح السجل
              </button>
            </div>
            <ul className="space-y-2">
              {history.map((h) => (
                <li
                  key={h.job_id}
                  data-testid={`pqg-history-item-${h.job_id}`}
                  className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white p-3 text-[11px] flex-wrap"
                >
                  <span
                    data-testid={`pqg-history-status-${h.job_id}`}
                    className={`px-2 py-0.5 rounded-full font-semibold ${
                      h.status === "ready"
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {(h.status || "?").toUpperCase()}
                  </span>
                  <span className="font-mono text-neutral-500 truncate max-w-[50%]">
                    {h.job_id}
                  </span>
                  {h.error_reason && (
                    <span className="text-red-700 font-mono">{h.error_reason}</span>
                  )}
                  {h.finished_at && (
                    <span className="text-neutral-400 ms-auto">{h.finished_at}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </AppShell>
  );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------
function StepHeader({ n, title }) {
  return (
    <div className="flex items-center gap-2 text-neutral-900 font-semibold">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full
                       bg-[#BC944A] text-white text-xs">{n}</span>
      {title}
    </div>
  );
}

function ConfigPanel({ c }) {
  return (
    <section data-testid="pqg-config-panel"
             className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 text-sm">
      <div className="flex items-center gap-2 text-neutral-700 mb-2">
        <Info className="w-4 h-4" /> الإعدادات المقفلة على الخادم
      </div>
      <ul className="grid grid-cols-2 gap-2 text-xs">
        <li><b>الموديل:</b> {c.model}</li>
        <li><b>الدقة:</b> {c.size}</li>
        <li><b>المدة:</b> {c.duration_seconds} ثوانٍ</li>
        <li><b>سعر الثانية:</b> {c.sar_per_second} SAR</li>
        <li className="col-span-2"><b>سقف الأمان:</b> {c.max_total_sar} SAR
          · <span className="text-neutral-500">{c.pricing_source}</span></li>
      </ul>
    </section>
  );
}

function CostRow({ label, value, testid, highlight, caption }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-sm text-neutral-700">{label}</span>
      <span data-testid={testid}
            className={`font-mono text-sm ${highlight ? "text-[#BC944A] font-bold" : "text-neutral-900"}`}>
        {value}
      </span>
      {caption && (
        <span className="basis-full text-[10px] text-neutral-500 text-start mt-0.5">
          {caption}
        </span>
      )}
    </div>
  );
}

function ConfirmCheck({ testid, checked, onChange, label }) {
  return (
    <label className="flex items-start gap-2 text-sm text-neutral-800 cursor-pointer">
      <input
        data-testid={testid}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 accent-[#BC944A]" />
      <span>{label}</span>
    </label>
  );
}

function ErrorBanner({ text }) {
  return (
    <div data-testid="pqg-error"
         className="rounded-xl bg-red-50 border border-red-200 text-red-800 p-3 text-sm flex gap-2">
      <XCircle className="w-4 h-4 mt-0.5" />
      <span>{text}</span>
    </div>
  );
}

function IncidentBanner({ statusCode, messageAr, incidentRef }) {
  return (
    <div
      data-testid="pqg-incident-banner"
      role="alert"
      className="rounded-2xl border-2 border-red-300 bg-red-50 text-red-900 p-5 space-y-2"
    >
      <div className="flex items-center gap-2 font-bold text-sm">
        <ShieldAlert className="w-5 h-5 text-red-700" />
        <span data-testid="pqg-incident-status">
          حالة الاختبار: {statusCode}
        </span>
      </div>
      <p data-testid="pqg-incident-message" className="text-sm leading-relaxed">
        {messageAr}
      </p>
      {incidentRef && (
        <p className="text-[11px] font-mono text-red-800/80">
          مرجع الحادثة: <span data-testid="pqg-incident-ref">{incidentRef}</span>
        </p>
      )}
    </div>
  );
}

function JobPanel({ job, apiBase, onDismiss }) {
  const ready = job.status === "ready";
  const failed = job.status === "failed";
  const pending = !ready && !failed;
  const videoSrc = ready && job.video_url ? `${apiBase}${job.video_url}` : null;
  const downloadSrc = ready && job.download_url ? `${apiBase}${job.download_url}` : null;
  return (
    <section data-testid="pqg-job-panel"
             className="rounded-2xl border border-neutral-200 bg-white p-5 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span data-testid="pqg-job-status" className={`px-2 py-0.5 rounded-full text-xs ${
          ready ? "bg-emerald-100 text-emerald-800" :
          failed ? "bg-red-100 text-red-800" :
          "bg-amber-100 text-amber-800"}`}>
          {(job.status || "?").toUpperCase()}
        </span>
        {pending && <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />}
        <span data-testid="pqg-job-id" className="text-xs text-neutral-500 font-mono">
          {job.job_id}
        </span>
        {(ready || failed) && (
          <button onClick={onDismiss} data-testid="pqg-job-dismiss"
                  className="ms-auto text-xs text-neutral-500 hover:text-neutral-800 underline">
            إغلاق
          </button>
        )}
      </div>

      {ready && (
        <div className="grid grid-cols-2 gap-2 text-xs text-neutral-700">
          <div><b>الموديل الفعلي:</b> {job.model_used || "—"}</div>
          <div><b>الأبعاد الفعلية:</b> {job.width}×{job.height}</div>
          <div><b>المدة الفعلية:</b> {job.duration_seconds} s</div>
          <div><b>حجم الملف:</b> {(job.file_size / 1024 / 1024).toFixed(2)} MB</div>
          <div><b>بدأ:</b> {job.started_at || "—"}</div>
          <div><b>انتهى:</b> {job.finished_at || "—"}</div>
        </div>
      )}
      {failed && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
             data-testid="pqg-job-error">
          <div><b>سبب الفشل:</b> {job.error_reason || "غير معروف"}</div>
          <div>{job.error_message || "—"}</div>
        </div>
      )}
      {ready && videoSrc && (
        <>
          <video data-testid="pqg-job-video"
                 controls preload="metadata"
                 className="w-full max-h-[80vh] rounded-xl border border-neutral-200 bg-black"
                 src={videoSrc} />
          <div className="flex gap-3 flex-wrap">
            <a data-testid="pqg-job-play" href={videoSrc} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#121822] text-white text-sm">
              <Play className="w-4 h-4" /> تشغيل بحجم كامل
            </a>
            <a data-testid="pqg-job-download" href={downloadSrc}
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neutral-300 text-sm">
              <Download className="w-4 h-4" /> تنزيل MP4
            </a>
          </div>
        </>
      )}
    </section>
  );
}
